import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { STRINGS } from '@/strings/strings';
import { redeemGiftCodeForBookReview } from '@/features/gifts/api';
import { useAuthStore } from '@/stores/authStore';
import { fireGa4BeginCheckout, getGa4Snapshot } from '@/lib/analytics/gtag';
import { getMetaSnapshot } from '@/lib/analytics/meta';
import {
  createBookCheckout,
  fetchBookBalancePreview,
  type BookBalancePreviewResponse,
  type BookCheckoutShippingAddress,
} from '../../api';
import { evaluateBookError } from '../../errors';
import type { OrderableSalesUnit, PreviewEnvelope } from '../../types';
import { CharacterCounter } from '../geschichte/CharacterCounter';
import {
  BOOK_CHECKOUT_SHIPPING_CITY_MAX_LENGTH,
  BOOK_CHECKOUT_SHIPPING_LINE_MAX_LENGTH,
  BOOK_CHECKOUT_SHIPPING_POSTAL_CODE_MAX_LENGTH,
  BOOK_CHECKOUT_SHIPPING_RECIPIENT_NAME_MAX_LENGTH,
} from '../../constants/text-limits';

/**
 * Bestellübersicht review screen (Sebastian ruling 2026-09-09
 * Bestellübersicht slice; P14 pay-with-balance additions 2026-09-24).
 *
 * Balance-preview lifecycle:
 *   1. On mount, fetch `GET /api/book/orders/:orderId/balance-preview`.
 *      Result names how much the customer's balance would cover for
 *      this order at this instant.
 *   2. When `balanceAppliedCents > 0`, the summary renders a
 *      "Guthaben: -X,XX €" row directly below "Gesamt", followed by a
 *      bold "Zu zahlen: X,XX €" row (always shown when balance is
 *      applied, including "0,00 €" on full cover — per ruling 5).
 *   3. When `paidWithBalance === true`, the shipping-address form
 *      renders below the exclusion block (Stripe never runs, so the
 *      PWA collects the address). DE-only, 5-digit PLZ regex, country
 *      as plain "Deutschland" text (per rulings 2 + 3).
 *   4. On "Kaufen", the shipping address is sent in the POST body
 *      (only on the fully-covered branch). The response
 *      `paidWithBalance` flag decides navigation: false →
 *      `window.location.href = checkoutUrl` (Stripe); true →
 *      `navigate("/book/:orderId/erfolg")` (no session_id query —
 *      BestellungErfolgRoute detects the missing query and skips the
 *      checkout-status poll).
 *
 * If the balance-preview fetch fails, the summary silently falls back
 * to the zero-balance shape — the customer still gets the Stripe path
 * without their balance applied. A background failure that hides
 * balance is safer than blocking checkout entirely.
 */
export interface BestellungReviewScreenProps {
  readonly envelope: PreviewEnvelope;
  readonly selectedUnit: OrderableSalesUnit;
  readonly onBack: () => void;
  readonly onCheckoutStart?: (slug: string | null) => void;
  /**
   * Sebastian ruling 2026-09-25 (E2E-test case D). Fires ONLY on the
   * balance-only branch (`paidWithBalance === true`) right AFTER the
   * successful `createBookCheckout` POST. Wired by `BestellungRoute`
   * to the shell context's `refetch` so the envelope refreshes
   * before the customer sees any downstream screen. Without this,
   * BookRoute at `/book/:orderId` reads the stale envelope (status=
   * preview_ready) and re-renders the same Bestellung tiles — the
   * customer thinks Kaufen failed and may click it again.
   *
   * The screen stays on itself with `redirecting=true` (Kaufen
   * button disabled). BestellungRoute's existing effect
   * (`envelope !== null && !canRender → navigate`) fires as soon as
   * the refetch resolves, taking the customer to the post-payment
   * wait screen at `/book/:orderId`. No zero-state flash.
   */
  readonly onPaidWithBalance?: () => void;
}

export function composeFormatLabel(unit: OrderableSalesUnit): string {
  const productClassLabel = productClassLabelFor(unit.productClass);
  const formatLabel = formatToDisplay(unit.format);
  return `${productClassLabel}, ${formatLabel}`;
}

function productClassLabelFor(productClass: OrderableSalesUnit['productClass']): string {
  switch (productClass) {
    case 'layflat':
      return STRINGS.book.bestellung.buchartLayflat;
    case 'pappbuch':
      return STRINGS.book.bestellung.buchartPappbuch;
    case 'hardcover':
      return STRINGS.book.bestellung.buchartHardcover;
  }
}

function formatToDisplay(format: string): string {
  const match = format.match(/^(\d+)x(\d+)$/);
  if (match === null) return format;
  return `${match[1]} × ${match[2]} cm`;
}

function formatEuros(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}

/**
 * Leading minus for the Guthaben row per ruling 4. Uses U+2212
 * (minus sign) rather than ASCII hyphen so the typography matches
 * the approved spec and the confirmation mail's own \u2212 convention
 * (Sebastian word-for-word approval 2026-10-20).
 */
function formatEurosNegative(cents: number): string {
  return `\u2212${formatEuros(cents)}`;
}

interface AddressFieldErrors {
  recipientName?: string;
  line1?: string;
  postalCode?: string;
  city?: string;
}

// Sebastian ruling 2026-11-02 — Austria launch. Country-specific
// postcode regexes applied once the customer has picked a country.
// DE: five digits; AT: four digits. Mirrored on the backend by
// `src/book/address/postcode-country-validator.ts`.
const POSTAL_CODE_REGEX_DE = /^\d{5}$/;
const POSTAL_CODE_REGEX_AT = /^\d{4}$/;

type ShippingCountry = 'DE' | 'AT';

function postalRegexFor(country: ShippingCountry): RegExp {
  return country === 'AT' ? POSTAL_CODE_REGEX_AT : POSTAL_CODE_REGEX_DE;
}

function postalErrorFor(
  country: ShippingCountry,
  s: typeof STRINGS.book.bestellung.review.shippingAddress,
): string {
  return country === 'AT' ? s.errorPostalFormatAt : s.errorPostalFormat;
}

export function BestellungReviewScreen({
  envelope,
  selectedUnit,
  onBack,
  onCheckoutStart,
  onPaidWithBalance,
}: BestellungReviewScreenProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [balancePreview, setBalancePreview] = useState<BookBalancePreviewResponse | null>(null);

  // P14 feature flag (Sebastian decision 2026-10-20) — the Dreamable-
  // Gutschein field is only visible when the backend has gifts
  // enabled. In production FEATURE_P14_GIFTS_ENABLED is OFF, so
  // customers must not see a field that cannot work. The backend
  // threads the resolved bit through /me as featureFlags.giftsEnabled;
  // older /me responses leave it undefined → gate reads as false.
  // Note: the Guthaben + "Zu zahlen" summary rows stay gated on
  // balanceAppliedCents > 0 as before — they can still render on a
  // flag-off account that already has balance from a prior redemption
  // via /einloesen (that path stays live regardless of this flag).
  const giftFieldEnabled = useAuthStore((s) => s.user?.featureFlags?.giftsEnabled ?? false);

  // P14 Dreamable-Gutschein field state (Sebastian decision 2026-10-20).
  // The field redeems a DREAM-code into the user's balance via the
  // review-screen endpoint and triggers a preview refresh so the row
  // updates immediately. `giftCodeStatus` encodes the three UX states:
  //   - 'idle'            → nothing to show below the field.
  //   - 'already_on_account' → soft info banner (same user re-entry).
  //   - 'error'           → verbatim backend user_message.
  // The input itself clears on success + already_on_account so the
  // customer can enter another voucher without manually erasing.
  const [giftCode, setGiftCode] = useState('');
  const [giftCodeSubmitting, setGiftCodeSubmitting] = useState(false);
  const [giftCodeStatus, setGiftCodeStatus] = useState<
    { kind: 'idle' } | { kind: 'already_on_account' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  // Shipping-address form state — populated only on the fully-covered
  // branch (paidWithBalance === true), but kept lifted so a customer
  // who toggles balance mid-session doesn't lose their typed input.
  const [recipientName, setRecipientName] = useState('');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [addressErrors, setAddressErrors] = useState<AddressFieldErrors>({});
  // Sebastian ruling 2026-11-02 — Austria launch. Country toggle sits
  // in local component state; NOT persisted via Zustand (per the task
  // directive "no persist version bump"). Deutschland preselected.
  // Drives the shipping amount shown in the summary + the delivery
  // text + the Stripe-side allowed_countries on the POST body.
  const [shippingCountry, setShippingCountry] = useState<ShippingCountry>('DE');

  // Shared preview fetcher (Sebastian ruling 2 2026-09-24; updated
  // 2026-09-25 — E2E-test case C finding 2). Used by BOTH the mount
  // effect AND the shortfall-race catch. On the shortfall path, the
  // same code re-runs so the summary rows + shipping-form visibility
  // flip based on the fresh preview:
  //   fresh preview paidWithBalance=false → form disappears, next
  //     Kaufen click runs the normal Stripe path (address collected
  //     by Stripe);
  //   fresh preview paidWithBalance=true  → form stays visible with
  //     the address the customer already typed (state preserved).
  //
  // Pass `selectedUnit.slug` so the backend can compute the preview
  // BEFORE `BookOrder.salesUnit` is written (that field only lands
  // at paid-flip — see backend `getBalancePreview`). Without this,
  // Guthaben / Zu zahlen would be silently missing on every fresh
  // review because the backend would throw
  // `book_order_not_checkoutable` and the catch below would blank
  // the preview.
  //
  // Deps include `envelope` (not just `envelope.bookOrderId`) so a
  // parent-side refetch — e.g. after `resetBookCheckoutCancelled`
  // in BestellungRoute — produces a new envelope object identity and
  // re-fires this effect (the balance may have moved on the server
  // side during the cancel round-trip).
  const refreshPreview = useCallback(async () => {
    try {
      // Sebastian ruling 2026-11-02 — pass the chosen country so the
      // backend's balance preview uses the country-specific shipping
      // amount. Omitted on older PWA builds = DE fallback on backend.
      const preview = await fetchBookBalancePreview(
        envelope.bookOrderId,
        selectedUnit.slug,
        shippingCountry,
      );
      setBalancePreview(preview);
    } catch {
      // Silent fallback to zero-balance display shape — the customer
      // still gets the Stripe path with no balance applied, safe.
      setBalancePreview(null);
    }
  }, [envelope, selectedUnit.slug, shippingCountry]);

  useEffect(() => {
    void refreshPreview();
  }, [refreshPreview]);

  // Sebastian ruling 2026-11-02 — per-country shipping amount for the
  // summary + the Lieferung line. The envelope exposes the full table
  // (one source of truth = BOOK_CONFIG.shipping_cents_by_country); the
  // Bestellübersicht picks the entry for the chosen country. Falls
  // back to `envelope.shippingCents` (DE default) if the backend has
  // not yet shipped the per-country map (older server build).
  const shippingCentsForCountry =
    envelope.shippingCentsByCountry?.[shippingCountry] ?? envelope.shippingCents;
  const totalCents = selectedUnit.priceCents + shippingCentsForCountry;
  const balanceAppliedCents = balancePreview?.balanceAppliedCents ?? 0;
  const amountChargedCents = balancePreview?.amountChargedCents ?? totalCents;
  const paidWithBalance = balancePreview?.paidWithBalance ?? false;

  function validateAddress(): { ok: true; address: BookCheckoutShippingAddress } | { ok: false } {
    const errors: AddressFieldErrors = {};
    const s = STRINGS.book.bestellung.review.shippingAddress;
    if (!recipientName.trim()) errors.recipientName = s.errorRecipientRequired;
    if (!line1.trim()) errors.line1 = s.errorLine1Required;
    if (!postalCode.trim()) errors.postalCode = s.errorPostalRequired;
    else if (!postalRegexFor(shippingCountry).test(postalCode.trim()))
      errors.postalCode = postalErrorFor(shippingCountry, s);
    if (!city.trim()) errors.city = s.errorCityRequired;
    setAddressErrors(errors);
    if (Object.keys(errors).length > 0) return { ok: false };
    return {
      ok: true,
      address: {
        recipientName: recipientName.trim(),
        line1: line1.trim(),
        line2: line2.trim() === '' ? null : line2.trim(),
        postalCode: postalCode.trim(),
        city: city.trim(),
        country: shippingCountry,
      },
    };
  }

  // Sebastian follow-up 2026-10-20: red validation messages must
  // disappear as soon as the field becomes valid, instead of hanging
  // around after the customer has typed the required value. Each
  // onChange wraps the raw setter so the matching error slot clears
  // on-input the moment the per-field rule passes. Rules mirror
  // validateAddress above exactly — single source of truth up there,
  // predicates below. The clear uses rest-spread (not `: undefined`)
  // because the project's exactOptionalPropertyTypes forbids writing
  // `undefined` into an optional-but-string slot explicitly.
  function clearError(key: keyof AddressFieldErrors) {
    setAddressErrors((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _dropped, ...rest } = prev;
      return rest;
    });
  }
  function onRecipientChange(v: string) {
    setRecipientName(v);
    if (addressErrors.recipientName !== undefined && v.trim().length > 0) {
      clearError('recipientName');
    }
  }
  function onLine1Change(v: string) {
    setLine1(v);
    if (addressErrors.line1 !== undefined && v.trim().length > 0) {
      clearError('line1');
    }
  }
  function onPostalChange(v: string) {
    setPostalCode(v);
    if (addressErrors.postalCode !== undefined && postalRegexFor(shippingCountry).test(v.trim())) {
      clearError('postalCode');
    }
  }
  // Country-toggle also clears any lingering postcode-format error
  // from the previous country (switching DE → AT must not strand the
  // "fünf Ziffern" message under a 4-digit AT postcode and vice
  // versa).
  function onCountryChange(next: ShippingCountry) {
    setShippingCountry(next);
    if (addressErrors.postalCode !== undefined && postalRegexFor(next).test(postalCode.trim())) {
      clearError('postalCode');
    }
  }
  function onCityChange(v: string) {
    setCity(v);
    if (addressErrors.city !== undefined && v.trim().length > 0) {
      clearError('city');
    }
  }

  const handleApplyGiftCode = async () => {
    const trimmed = giftCode.trim();
    if (trimmed === '' || giftCodeSubmitting || redirecting) return;
    setGiftCodeSubmitting(true);
    setGiftCodeStatus({ kind: 'idle' });
    try {
      const result = await redeemGiftCodeForBookReview(trimmed);
      // Both outcomes refresh the preview so the row + "Zu zahlen"
      // update immediately. The input clears so the next voucher
      // can be entered without manual erase.
      await refreshPreview();
      setGiftCode('');
      if (result.outcome === 'already_on_account') {
        setGiftCodeStatus({ kind: 'already_on_account' });
      } else {
        setGiftCodeStatus({ kind: 'idle' });
      }
    } catch (err) {
      // Verbatim backend wording from the catalog (gift_code_not_found,
      // gift_code_expired, gift_code_refunded, gift_redeem_rate_limited).
      // Falls back to the review-screen generic error for non-envelope
      // transport failures.
      const message =
        err instanceof ApiError && typeof err.envelope?.user_message === 'string'
          ? err.envelope.user_message
          : STRINGS.book.bestellung.review.error;
      setGiftCodeStatus({ kind: 'error', message });
    } finally {
      setGiftCodeSubmitting(false);
    }
  };

  const handleKaufen = async () => {
    if (!acknowledged || redirecting) return;
    // Only when we're on the fully-covered branch: require + validate
    // the shipping address before the POST.
    let addressPayload: BookCheckoutShippingAddress | undefined;
    if (paidWithBalance) {
      const validation = validateAddress();
      if (!validation.ok) return;
      addressPayload = validation.address;
    }
    setRedirecting(true);
    setErrorMessage(null);
    onCheckoutStart?.(selectedUnit.slug);
    // GA4 funnel — begin_checkout (Sebastian ruling 2026-10-21).
    // Fires BEFORE the POST on both paths (Stripe + balance-only) so
    // the funnel captures intent regardless of what happens next. Uses
    // `amountChargedCents` from the balance-preview as `value`: on the
    // balance-only path that's 0 (correct, matches the "no money
    // charged" state).
    fireGa4BeginCheckout({
      value: amountChargedCents / 100,
      currency: 'EUR',
      items: [
        {
          item_id: selectedUnit.slug,
          item_name: composeFormatLabel(selectedUnit),
          item_variant: selectedUnit.productClass,
          price: selectedUnit.priceCents / 100,
          quantity: 1,
        },
      ],
    });
    // Capture the GA4 snapshot (client_id, session_id, consent) and
    // thread it into the POST body. The server stores it on the row
    // / in Stripe metadata; the server-side MP listener on
    // BookPaidEvent reads it from the row and gates on consent.
    const ga4Snapshot = await getGa4Snapshot();
    const ga4Payload = {
      ...(ga4Snapshot.clientId !== null ? { clientId: ga4Snapshot.clientId } : {}),
      ...(ga4Snapshot.sessionId !== null ? { sessionId: ga4Snapshot.sessionId } : {}),
      consentAnalytics: ga4Snapshot.consentAnalytics,
      consentAds: ga4Snapshot.consentAds,
    };
    // Meta CAPI snapshot (2026-10-29). fbc/fbp only forwarded when
    // marketing consent is granted — the backend persists them under
    // the same gate, so the no-consent path produces a byte-identical
    // request to the pre-feature shape apart from the explicit
    // consentMarketing:false signal.
    const metaSnapshot = getMetaSnapshot();
    const metaPayload = {
      consentMarketing: metaSnapshot.consentMarketing,
      ...(metaSnapshot.consentMarketing && metaSnapshot.fbc !== null
        ? { fbc: metaSnapshot.fbc }
        : {}),
      ...(metaSnapshot.consentMarketing && metaSnapshot.fbp !== null
        ? { fbp: metaSnapshot.fbp }
        : {}),
    };
    try {
      const response = await createBookCheckout(
        envelope.bookOrderId,
        selectedUnit.slug,
        true,
        addressPayload,
        ga4Payload,
        metaPayload,
        shippingCountry,
      );
      if (response.paidWithBalance) {
        // Sebastian ruling 2026-09-25 (E2E-test case D). No Stripe
        // hop — the order is already at status `paid`/`rendering_full`
        // server-side. Refetch the shell envelope so BookRoute at
        // `/book/:orderId` reads the fresh status; BestellungRoute's
        // own effect (`envelope !== null && !canRender → navigate`)
        // then takes the customer to the post-payment wait screen.
        // Keep `redirecting=true` — the Kaufen button stays disabled
        // until the parent route unmounts this screen.
        //
        // Pre-2026-09-25 the branch navigated to `/book/:orderId/erfolg`
        // which then re-navigated to `/book/:orderId`, but the shell
        // envelope was stale (status=preview_ready) and BookRoute
        // re-dispatched to the Bestellung tiles — the customer saw the
        // old preview and thought Kaufen had failed.
        onPaidWithBalance?.();
        return;
      }
      if (response.checkoutUrl === null) {
        // Response contract violation — paidWithBalance=false MUST
        // carry a checkoutUrl. Recover gracefully with an error.
        setRedirecting(false);
        setErrorMessage(STRINGS.book.bestellung.review.errorPaidWithBalanceUnexpected);
        return;
      }
      window.location.href = response.checkoutUrl;
    } catch (err) {
      setRedirecting(false);
      // Sebastian ruling 2 2026-09-24 — on book_order_balance_shortfall_race,
      // re-fetch the balance preview so the summary rows update
      // (Guthaben / Zu zahlen) and the shipping-form visibility flips
      // to match the fresh coverage. NEVER auto-continue: the
      // customer sees the updated numbers + the banner message and
      // clicks Kaufen again against the new amount.
      if (
        err instanceof ApiError &&
        err.envelope?.error_code === 'book_order_balance_shortfall_race'
      ) {
        await refreshPreview();
      }
      const message =
        err instanceof ApiError && err.envelope !== null
          ? evaluateBookError(err).userMessage
          : STRINGS.book.bestellung.review.error;
      setErrorMessage(message);
    }
  };

  const shippingAddressStrings = STRINGS.book.bestellung.review.shippingAddress;

  return (
    <div className="space-y-6 py-4">
      <button
        type="button"
        onClick={onBack}
        disabled={redirecting}
        className="text-sm text-dream-navy underline disabled:opacity-60"
      >
        {STRINGS.book.bestellung.review.back}
      </button>

      <header>
        <h1 className="text-h3 leading-tight text-dream-navy">
          {STRINGS.book.bestellung.review.heading}
        </h1>
      </header>

      {/* P14 Dreamable-Gutschein field (Sebastian decision 2026-10-20).
          Sits above the price summary so the balance row visibly updates
          once a code is applied. The field accepts the raw code
          (hyphenated or not, any case) — backend normalises. Partial
          vouchers stack: enter a second code, press Einlösen again.
          Only renders when the backend has gifts enabled (flag off =
          field hidden; the matching /api/gifts/redeem-for-book-review
          endpoint is also unregistered in that case). */}
      {giftFieldEnabled && (
        <section className="space-y-2 rounded-lg border border-dream-navy/15 p-4">
          <label htmlFor="gift-code-input" className="text-sm font-medium text-dream-navy">
            {STRINGS.book.bestellung.review.giftCode.label}
          </label>
          <div className="flex gap-2">
            <input
              id="gift-code-input"
              type="text"
              value={giftCode}
              onChange={(e) => setGiftCode(e.target.value)}
              placeholder={STRINGS.book.bestellung.review.giftCode.placeholder}
              disabled={giftCodeSubmitting || redirecting}
              autoComplete="off"
              spellCheck={false}
              className="flex-1 rounded-md border border-dream-navy/20 px-3 py-2 text-sm text-dream-navy placeholder:text-dream-navy/40 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={handleApplyGiftCode}
              disabled={giftCode.trim() === '' || giftCodeSubmitting || redirecting}
              className="rounded-md bg-dream-navy px-4 py-2 text-sm font-medium text-dream-white hover:bg-dream-navy/90 disabled:opacity-60"
            >
              {STRINGS.book.bestellung.review.giftCode.button}
            </button>
          </div>
          <p className="text-xs text-dream-navy/70">
            {STRINGS.book.bestellung.review.giftCode.hint}
          </p>
          {giftCodeStatus.kind === 'already_on_account' && (
            <p
              className="rounded-md border border-dream-navy/15 bg-dream-background px-3 py-2 text-sm text-dream-navy"
              data-testid="gift-code-already-on-account"
            >
              {STRINGS.book.bestellung.review.giftCode.alreadyOnAccount}
            </p>
          )}
          {giftCodeStatus.kind === 'error' && (
            <p
              className="rounded-md border border-error px-3 py-2 text-sm text-dream-navy"
              data-testid="gift-code-error"
            >
              {giftCodeStatus.message}
            </p>
          )}
        </section>
      )}

      <section className="space-y-2 rounded-lg border border-dream-navy/15 p-4">
        <SummaryRow label={STRINGS.book.bestellung.review.labelBook} value={envelope.title} />
        <SummaryRow
          label={STRINGS.book.bestellung.review.labelFormat}
          value={composeFormatLabel(selectedUnit)}
        />
        <SummaryRow
          label={STRINGS.book.bestellung.review.labelPrice}
          value={formatEuros(selectedUnit.priceCents)}
        />
        <SummaryRow
          label={STRINGS.book.bestellung.review.labelShipping}
          value={formatEuros(shippingCentsForCountry)}
        />
        <div className="mt-2 border-t border-dream-navy/15 pt-2">
          <SummaryRow
            label={STRINGS.book.bestellung.review.labelTotal}
            value={formatEuros(totalCents)}
            bold
          />
          <p className="mt-1 text-right text-xs text-dream-navy/70">
            {STRINGS.book.bestellung.review.vatNote}
          </p>
        </div>
        {balanceAppliedCents > 0 && (
          <>
            {/* mt-4 (not the sibling mt-2) because the preceding block
                ends in the 7-% VAT note, which already occupies its
                own text line. The matching mt-2 separator sits visibly
                too tight below that extra line; mt-4 restores parity
                with the gaps between the other blocks in the card
                (Sebastian follow-up 2026-10-20). */}
            <div className="mt-4 border-t border-dream-navy/15 pt-2">
              <SummaryRow
                label={STRINGS.book.bestellung.review.labelBalance}
                value={formatEurosNegative(balanceAppliedCents)}
              />
              <SummaryRow
                label={STRINGS.book.bestellung.review.labelAmountToPay}
                value={formatEuros(amountChargedCents)}
                bold
              />
            </div>
          </>
        )}
        <div className="mt-2 border-t border-dream-navy/15 pt-2">
          <SummaryRow
            label={STRINGS.book.bestellung.review.labelDelivery}
            value={
              shippingCountry === 'AT'
                ? STRINGS.book.bestellung.review.deliveryTextAt
                : STRINGS.book.bestellung.review.deliveryText
            }
          />
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-medium text-dream-navy">
          {STRINGS.book.bestellung.review.exclusionHeading}
        </h2>
        <p className="text-sm text-dream-navy">{STRINGS.book.bestellung.review.exclusionBody}</p>
      </section>

      {paidWithBalance && (
        <section
          className="space-y-3 rounded-lg border border-dream-navy/15 p-4"
          data-testid="shipping-address-form"
        >
          <h2 className="text-base font-medium text-dream-navy">
            {shippingAddressStrings.heading}
          </h2>
          <p className="text-sm text-dream-navy/80">{shippingAddressStrings.intro}</p>
          <AddressField
            id="ship-recipient"
            label={shippingAddressStrings.labelRecipient}
            value={recipientName}
            onChange={onRecipientChange}
            error={addressErrors.recipientName}
            disabled={redirecting}
            autoComplete="name"
            maxLength={BOOK_CHECKOUT_SHIPPING_RECIPIENT_NAME_MAX_LENGTH}
            showCounter
          />
          <AddressField
            id="ship-line1"
            label={shippingAddressStrings.labelLine1}
            value={line1}
            onChange={onLine1Change}
            error={addressErrors.line1}
            disabled={redirecting}
            autoComplete="address-line1"
            maxLength={BOOK_CHECKOUT_SHIPPING_LINE_MAX_LENGTH}
            showCounter
          />
          <AddressField
            id="ship-line2"
            label={shippingAddressStrings.labelLine2}
            value={line2}
            onChange={setLine2}
            error={undefined}
            disabled={redirecting}
            autoComplete="address-line2"
            placeholder={shippingAddressStrings.placeholderLine2}
            maxLength={BOOK_CHECKOUT_SHIPPING_LINE_MAX_LENGTH}
            showCounter
          />
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              {/* Postcode: maxLength=5, no counter (Sebastian ruling
                  2026-09-29 §2). Existing /^\d{5}$/ regex validation
                  on submit stays in place. */}
              <AddressField
                id="ship-postal"
                label={shippingAddressStrings.labelPostalCode}
                value={postalCode}
                onChange={onPostalChange}
                error={addressErrors.postalCode}
                disabled={redirecting}
                autoComplete="postal-code"
                inputMode="numeric"
                maxLength={BOOK_CHECKOUT_SHIPPING_POSTAL_CODE_MAX_LENGTH}
              />
            </div>
            <div className="col-span-2">
              <AddressField
                id="ship-city"
                label={shippingAddressStrings.labelCity}
                value={city}
                onChange={onCityChange}
                error={addressErrors.city}
                disabled={redirecting}
                autoComplete="address-level2"
                maxLength={BOOK_CHECKOUT_SHIPPING_CITY_MAX_LENGTH}
                showCounter
              />
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-dream-navy/70">
              {shippingAddressStrings.labelCountry}
            </div>
            {/* Sebastian ruling 2026-11-02 — two-option country
                toggle. Rendered as radio buttons (not a disabled
                select); Deutschland preselected via useState default.
                Both options stay enabled; switching re-fires the
                balance preview so the summary + delivery line update
                immediately. */}
            <div className="mt-1 flex gap-4 text-sm text-dream-navy" data-testid="ship-country">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="ship-country"
                  value="DE"
                  checked={shippingCountry === 'DE'}
                  onChange={() => onCountryChange('DE')}
                  disabled={redirecting}
                />
                {shippingAddressStrings.countryDeutschland}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="ship-country"
                  value="AT"
                  checked={shippingCountry === 'AT'}
                  onChange={() => onCountryChange('AT')}
                  disabled={redirecting}
                />
                {shippingAddressStrings.countryOesterreich}
              </label>
            </div>
          </div>
          <p className="text-xs text-dream-navy/70">{shippingAddressStrings.countryNote}</p>
        </section>
      )}
      {/* Sebastian ruling 2026-11-02 — Stripe path toggle. On the
          balance-only branch the toggle lives inside the shipping
          address section above (customer must pick a country before
          Stripe is skipped). On the Stripe path the shipping address
          form is NOT rendered (Stripe collects it), but the customer
          still needs to pick the country so backend's
          allowed_countries + shipping amount resolve. One shared
          state variable, two render sites. */}
      {!paidWithBalance && (
        <section
          className="space-y-2 rounded-lg border border-dream-navy/15 p-4"
          data-testid="ship-country-stripe"
        >
          <div className="text-xs font-medium text-dream-navy/70">
            {shippingAddressStrings.labelCountry}
          </div>
          <div className="mt-1 flex gap-4 text-sm text-dream-navy">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="ship-country"
                value="DE"
                checked={shippingCountry === 'DE'}
                onChange={() => onCountryChange('DE')}
                disabled={redirecting}
              />
              {shippingAddressStrings.countryDeutschland}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="ship-country"
                value="AT"
                checked={shippingCountry === 'AT'}
                onChange={() => onCountryChange('AT')}
                disabled={redirecting}
              />
              {shippingAddressStrings.countryOesterreich}
            </label>
          </div>
          <p className="text-xs text-dream-navy/70">{shippingAddressStrings.countryNote}</p>
        </section>
      )}

      <label className="flex items-start gap-3 text-sm text-dream-navy">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          disabled={redirecting}
          className="mt-1 h-4 w-4 shrink-0"
        />
        <span>{STRINGS.book.bestellung.review.acknowledgmentCheckbox}</span>
      </label>

      {errorMessage !== null && (
        <p className="rounded-lg border border-error px-4 py-3 text-sm text-dream-navy">
          {errorMessage}
        </p>
      )}

      <div className="pt-2">
        <button
          type="button"
          onClick={handleKaufen}
          disabled={!acknowledged || redirecting}
          className="h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60"
        >
          {paidWithBalance
            ? STRINGS.book.bestellung.review.ctaBalanceOnly
            : STRINGS.book.bestellung.review.cta}
        </button>
        {redirecting && (
          <p className="mt-2 text-center text-xs text-dream-navy/70">
            {STRINGS.book.bestellung.redirectingNote}
          </p>
        )}
      </div>
    </div>
  );
}

interface SummaryRowProps {
  readonly label: string;
  readonly value: string;
  readonly bold?: boolean;
}

function SummaryRow({ label, value, bold }: SummaryRowProps) {
  return (
    <div
      className={`flex justify-between gap-4 text-sm text-dream-navy ${bold ? 'font-semibold' : ''}`}
    >
      <span className="shrink-0">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

interface AddressFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly error: string | undefined;
  readonly disabled: boolean;
  readonly autoComplete: string;
  readonly placeholder?: string;
  readonly inputMode?: 'numeric' | 'text';
  /**
   * fix/text-field-limits (Sebastian ruling 2026-09-29) — hard cap
   * on typing + pasting. Sourced from the DTO's exported constant
   * via `constants/text-limits.ts`.
   */
  readonly maxLength?: number;
  /**
   * fix/text-field-limits — when true and `maxLength` is set, renders
   * the shared `CharacterCounter` below the input. Off on the 5-digit
   * postcode input where a counter adds no value (Sebastian ruling
   * 2026-09-29 §2).
   */
  readonly showCounter?: boolean;
}

function AddressField({
  id,
  label,
  value,
  onChange,
  error,
  disabled,
  autoComplete,
  placeholder,
  inputMode,
  maxLength,
  showCounter,
}: AddressFieldProps) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-dream-navy/70">
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoComplete={autoComplete}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        className="w-full rounded-lg border border-dream-navy/20 bg-white px-3 py-2 text-sm text-dream-navy focus:border-dream-navy focus:outline-none disabled:opacity-60"
        aria-invalid={error !== undefined}
        aria-describedby={error !== undefined ? `${id}-error` : undefined}
      />
      {showCounter === true && maxLength !== undefined && (
        <CharacterCounter value={value.length} corridor={{ min: 0, max: maxLength }} />
      )}
      {error !== undefined && (
        <p id={`${id}-error`} className="text-xs text-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
